import { UnitText } from '../interfaces/panel-data';

/**
 * Wording for counts that carry a unit.
 *
 * The pipeline's stages lose different things — Crawl loses pages, Scrape loses SKUs, WES loses records
 * — so a bar reading "1,247 lost" tells a reader a number without telling them what it counts. Every
 * count the funnel renders goes through here so the unit comes from the backend's configured vocabulary
 * rather than from a component's assumption.
 *
 * Nothing here invents a unit. A stage with none configured renders the bare number, because a stand-in
 * like "items" would be a guess presented as fact, and wrong wherever the stage counts pages.
 */

/** True when a unit is configured and not just whitespace. */
const hasUnit = (text: UnitText): boolean => !!text.unit && text.unit.trim().length > 0;

/**
 * The unit in the form that agrees with `count`, or null when no unit is configured.
 *
 * Zero takes the plural, as English does ("0 pages lost"). `unitPlural` wins when present, which is the
 * only way to get "entities" rather than "entitys"; a regular plural leaves it null and appends "s".
 * Casing is left exactly as configured, so an acronym unit stays "SKUs" rather than becoming "Skus".
 */
export const pluraliseUnit = (text: UnitText, count: number): string | null => {
  if (!hasUnit(text)) return null;
  const singular = text.unit as string;
  if (count === 1) return singular;
  return text.unitPlural || `${singular}s`;
};

/**
 * A count with its unit, e.g. `1,247 pages` — or the bare `1,247` when the stage has no unit yet.
 *
 * Deliberately not "1,247 items lost": see the note above on inventing units.
 */
export const formatUnitCount = (count: number, text: UnitText): string => {
  const unit = pluraliseUnit(text, count);
  const formatted = count.toLocaleString();
  return unit ? `${formatted} ${unit}` : formatted;
};

/** The plural unit for use as a standalone label, or null when there is no unit to label. */
export const unitLabel = (text: UnitText): string | null => pluraliseUnit(text, 0);
